import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Cloud, Lock, Eye, EyeOff, CheckCircle, XCircle, 
  Shield, AlertTriangle, Loader2, Database, Server, Trash2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple encryption for localStorage (not for production secrets, just obfuscation)
const encryptCredentials = (creds) => {
  return btoa(JSON.stringify(creds));
};

const decryptCredentials = (encrypted) => {
  try {
    return JSON.parse(atob(encrypted));
  } catch {
    return null;
  }
};

const AWSConnect = ({ isOpen, onClose, onConnected }) => {
  const [credentials, setCredentials] = useState({
    access_key_id: '',
    secret_access_key: '',
    region: 'us-east-1'
  });
  const [showSecret, setShowSecret] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('aws_creds');
    if (saved) {
      const decrypted = decryptCredentials(saved);
      if (decrypted) {
        setCredentials(decrypted);
        setRememberCredentials(true);
      }
    }
  }, []);

  const handleQuickTest = async () => {
    if (!credentials.access_key_id || !credentials.secret_access_key) {
      toast.error('Please fill in credentials first');
      return;
    }

    setIsTesting(true);
    setTestResults(null);

    try {
      const response = await axios.post(`${API_URL}/api/aws/quick-test`, credentials);
      setTestResults(response.data);
      
      if (response.data.success) {
        toast.success('All permissions verified!');
      } else {
        toast.warning(response.data.fix_suggestion || 'Some permissions missing');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Test failed';
      setTestResults({
        success: false,
        error: message,
        fix_suggestion: 'Check your credentials'
      });
      toast.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!credentials.access_key_id || !credentials.secret_access_key) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/aws/connect`, credentials);
      
      if (response.data.success) {
        setConnectionStatus({
          success: true,
          ...response.data.account_info
        });

        // Save to localStorage if requested (encrypted)
        if (rememberCredentials) {
          localStorage.setItem('aws_creds', encryptCredentials(credentials));
        } else {
          localStorage.removeItem('aws_creds');
        }

        // Store credentials in sessionStorage for this session only
        sessionStorage.setItem('aws_session', encryptCredentials(credentials));

        toast.success('Connected to AWS successfully!');
        
        if (onConnected) {
          onConnected(credentials, response.data.account_info);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({
        success: false,
        error: message
      });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClearSaved = () => {
    localStorage.removeItem('aws_creds');
    sessionStorage.removeItem('aws_session');
    setCredentials({
      access_key_id: '',
      secret_access_key: '',
      region: 'us-east-1'
    });
    setRememberCredentials(false);
    setConnectionStatus(null);
    toast.success('Saved credentials cleared');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="aws-connect-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-orange-500" />
            Connect to AWS
          </DialogTitle>
          <DialogDescription>
            Enter your AWS credentials to view real-time cost data from Cost Explorer.
          </DialogDescription>
        </DialogHeader>

        {/* Security Notice */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Your data stays secure
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Credentials are stored only in your browser. We query AWS directly - 
                <strong> no data is ever stored on our servers.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          {/* Access Key ID */}
          <div className="space-y-2">
            <Label htmlFor="access_key_id">Access Key ID *</Label>
            <Input
              id="access_key_id"
              placeholder="AKIAIOSFODNN7EXAMPLE"
              value={credentials.access_key_id}
              onChange={(e) => setCredentials({ ...credentials, access_key_id: e.target.value })}
              data-testid="aws-access-key-input"
            />
            <p className="text-xs text-slate-400">
              Found in AWS IAM Console → Users → Security credentials
            </p>
          </div>

          {/* Secret Access Key */}
          <div className="space-y-2">
            <Label htmlFor="secret_access_key">Secret Access Key *</Label>
            <div className="relative">
              <Input
                id="secret_access_key"
                type={showSecret ? 'text' : 'password'}
                placeholder="••••••••••••••••"
                value={credentials.secret_access_key}
                onChange={(e) => setCredentials({ ...credentials, secret_access_key: e.target.value })}
                data-testid="aws-secret-key-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-400"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Region */}
          <div className="space-y-2">
            <Label htmlFor="region">Region (optional)</Label>
            <Input
              id="region"
              placeholder="us-east-1"
              value={credentials.region}
              onChange={(e) => setCredentials({ ...credentials, region: e.target.value })}
            />
            <p className="text-xs text-slate-400">
              Cost Explorer API always uses us-east-1 regardless of this setting
            </p>
          </div>

          {/* IAM Policy Hint */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Required IAM Permissions:</strong> ce:GetCostAndUsage, ce:GetCostForecast
            </p>
            <a 
              href="https://docs.aws.amazon.com/cost-management/latest/userguide/ce-api.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-amber-600 hover:underline flex items-center gap-1 mt-1"
            >
              View AWS documentation <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Quick Test Button */}
          <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Quick Permission Test</p>
                <p className="text-xs text-slate-400">Validate credentials and IAM permissions</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleQuickTest}
                disabled={isTesting || !credentials.access_key_id || !credentials.secret_access_key}
                data-testid="aws-quick-test-btn"
              >
                {isTesting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-1" />
                    Quick Test
                  </>
                )}
              </Button>
            </div>
            
            {/* Test Results */}
            {testResults && (
              <div className={`mt-3 p-2 rounded text-sm ${
                testResults.success 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              }`}>
                {testResults.success ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      All permissions verified!
                    </div>
                    {testResults.tests && (
                      <div className="text-xs space-y-0.5 ml-5">
                        <p>✓ Credentials Valid</p>
                        <p>✓ Cost Explorer Access</p>
                        {testResults.account_info && (
                          <p className="text-slate-400 dark:text-slate-400">
                            Account: {testResults.account_info.account_id}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 font-medium">
                      <XCircle className="w-4 h-4" />
                      {testResults.error || 'Test failed'}
                    </div>
                    {testResults.fix_suggestion && (
                      <p className="text-xs ml-5">
                        Fix: {testResults.fix_suggestion}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Remember Credentials */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-400 dark:text-slate-400">
                Remember credentials (browser only)
              </span>
            </label>
            {localStorage.getItem('aws_creds') && (
              <Button variant="ghost" size="sm" onClick={handleClearSaved} className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear saved
              </Button>
            )}
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <div className={`p-3 rounded-lg border ${
              connectionStatus.success 
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {connectionStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    connectionStatus.success ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {connectionStatus.success ? 'Connected Successfully' : 'Connection Failed'}
                  </p>
                  {connectionStatus.success ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 space-y-0.5">
                      <p>Region: {connectionStatus.region}</p>
                      <p>Cost Data Available: {connectionStatus.has_cost_data ? 'Yes' : 'No'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-red-600">{connectionStatus.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            disabled={isConnecting}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="aws-connect-btn"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4 mr-2" />
                Connect
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hook to get current AWS credentials from session
export const useAWSCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Check session storage first
    const sessionCreds = sessionStorage.getItem('aws_session');
    if (sessionCreds) {
      const decrypted = decryptCredentials(sessionCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
        return;
      }
    }
    
    // Fall back to localStorage
    const savedCreds = localStorage.getItem('aws_creds');
    if (savedCreds) {
      const decrypted = decryptCredentials(savedCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
      }
    }
  }, []);

  const disconnect = () => {
    sessionStorage.removeItem('aws_session');
    setCredentials(null);
    setIsConnected(false);
  };

  const updateCredentials = (newCreds) => {
    setCredentials(newCreds);
    setIsConnected(true);
    sessionStorage.setItem('aws_session', encryptCredentials(newCreds));
  };

  return { credentials, isConnected, disconnect, updateCredentials };
};

export default AWSConnect;
